import {
    AfterContentInit,
    ChangeDetectorRef,
    Component,
    ContentChild,
    ContentChildren,
    EventEmitter,
    Injector,
    Input,
    OnDestroy,
    OnInit,
    Optional,
    Output,
    QueryList,
    Type
} from "@angular/core";
import {ActivatedRoute, NavigationStart, Router} from "@angular/router";
import {EntityData, EntityModel, SearchModel, SearchSortField} from "@fedeltapointofsale/data-client";
import {Observable, Subscription} from "rxjs";
import {PageGridChangeEvent, SearchGridComponent, SortGridChangeEvent} from "../search-grid/search-grid.component";
import {SearchActionEvent} from "./search-action-event";
import {
    FUIModalsService,
    FUISpinnersService,
    FUIStackedPageContextService,
    FUIStackedPageService
} from "@fedeltapointofsale/ui";
import {EditPageComponent} from "../../edit/edit-page/edit-page.component";
import {EditParams} from "../../edit/edit-page/edit-params";
import {EditMode} from "../../edit/edit-page/edit-mode";
import {ExtendedSearchParams, SearchType} from "./extended-search-params";
import {RoutingDiscoveryService} from "../../../../routing-discovery.service";
import {ErrorHandlerService} from "../../../../error-handler.service";
import {AnalyticsService} from "../../../../analytics/analytics.service";
import {AnalyticEvent} from "../../../../analytics/analytic-event";
import {ActionEvent} from "../../action.event";
import {PageParameterService} from "../../page-parameter.service";
import {ReportGenerationService} from "../../report/report-generation.service";
import {SearchPageService} from "./search-page.service";
import {SearchFormExtendedService} from "../search-form/search-form-extended.service";
import {EntityActionsComponent} from "../../actions/entity-actions.component";
import {EntityActionComponent} from "../../actions/entity-action.component";

/**
 *  Orchestrates a search using key components as SearchForm and SearchGrid.
 */
@Component({
    selector: "wbo-search-page",
    styleUrls: ["./search-page.component.scss"],
    templateUrl: "./search-page.component.html",
    providers: [
        SearchPageService,
        ReportGenerationService,
        SearchFormExtendedService
    ]
})
export class SearchPageComponent implements OnInit, AfterContentInit, OnDestroy {
    private static readonly INITIAL_LIMIT_PAGE = 25;

    /**
     * Defines when a pending search is running
     */
    searching: boolean;
    spinning: boolean;

    /**
     * Defines when a new search request was made while a pending search is running
     */
    requestedSearch: boolean;

    @ContentChild(SearchGridComponent, {static: true}) searchGridComponent: SearchGridComponent;

    /** Can be used to set the initial filter parameters for the search **/
    @Output() prepareInitialFilter: EventEmitter<any> = new EventEmitter<any>();

    /**
     * Emitted when Add New has been pressed before the default action has been performed.
     * If a the Add New has been handled by a higher level component then set the "confirm" flag to false
     */
    @Output() onAddNew: EventEmitter<ActionEvent> = new EventEmitter<ActionEvent>();

    /**
     * Emitted when the open entity button is pressed but there is no edit page.
     */
    @Output() onOpen: EventEmitter<SearchActionEvent> = new EventEmitter<SearchActionEvent>();

    /**
     * Title override. Typically should not be specified as the default behaviour is usually suitable.
     */
    @Input() title: string;

    /**
     * Title description. Typically should not be specified. This may be set if search pages are used in a specific
     * context that is away from typical CRUD.
     */
    @Input() description: string;

    /**
     * Override the edit route
     */
    private _editRoute: string;

    /**
     * Edit params when adding a new entity
     */
    @Input() editParams: any;

    /**
     * Disable add button
     */
    @Input() disableAddButton: boolean;

    /**
     * Force display of add button
     */
    @Input() showAddButton: boolean;

    /**
     * Disable open button
     */
    @Input() disableOpenButton: boolean;

    /**
     * Force display of open button
     */
    @Input() showOpenButton: boolean;

    /**
     * Indicates that the component will manage displaying the edit screen
     */
    @Input() managedEdit: boolean;

    @Input() searchOnOpen: boolean = true;

    @Input() overrideManualRefresh: boolean = false;

    /**
     * If specified then this edit page is used instead of the derived edit page. Note that if the edit page is defined
     * in the router then #editRoute should be used instead. If only editPage is provided without editRoute, then
     * the edit page can only be displayed using the stacked view.
     *
     * Typically editPage should not be specified to allow the edit page to be derived by convention.
     */
    @Input() editPage: Type<any>;

    /**
     * Event emitted before editing an entity. Default behaviour can prevented.
     */
    @Output() editing: EventEmitter<ActionEvent> = new EventEmitter<ActionEvent>();

    /**
     * Event emitted when the search is navigated to
     */
    @Output() navigatedTo: EventEmitter<any> = new EventEmitter<any>();

    @Output() onManualRefresh: EventEmitter<void> = new EventEmitter<void>();

    @ContentChildren(EntityActionsComponent) entityActionsComponent: QueryList<EntityActionsComponent>;

    public hasHeaderToggle: boolean;
    selectedRecords: any[] = [];
    private subscriptions: Subscription = new Subscription();

    // keep the search route path to compare when navigating back and refreshing the filter
    private searchRoutePath: string;

    private _autoRefreshInterval: number;
    private autoRefreshTimeout: ReturnType<typeof setTimeout>;
    private _autoRefreshEnabled: boolean = true;

    entityActions: EntityActionComponent[] = [];
    private entityActionsSubscription: Subscription;

    hasInitialised: boolean;
    hasSearchStarted: boolean;

    constructor(private changeDetectorRef: ChangeDetectorRef,
                private route: ActivatedRoute,
                private router: Router,
                private spinnersService: FUISpinnersService,
                private modalsService: FUIModalsService,
                public searchModel: SearchModel<any>,
                @Optional() private entityModel: EntityModel<any>,
                private stackedPageService: FUIStackedPageService,
                @Optional() public stackedPageContextService: FUIStackedPageContextService,
                private routingDiscoveryService: RoutingDiscoveryService,
                private errorHandlerService: ErrorHandlerService,
                private analyticsService: AnalyticsService,
                private searchPageService: SearchPageService,
                public reportGenerationService: ReportGenerationService,
                private injector: Injector) {
        this.hasHeaderToggle = false;

        if (!this._editRoute) {
            this._editRoute = this.searchModel.getResource();
        }

        if (!this.editPage) {
            this.editPage = this.routingDiscoveryService.findRoutingComponent(this._editRoute);
        }

        this.subscriptions.add(route.url.subscribe((urlSegments) => {
            this.searchRoutePath = urlSegments.map((url) => url.path).join("/");
        }));
        // refreshes the search when navigating back
        this.subscriptions.add(this.router.events.subscribe((event) => {
            if (event instanceof NavigationStart) {
                if (event.url.endsWith(this.searchRoutePath)) {
                    this.navigatedTo.emit();
                    if (this.searching) {
                        this.spinnersService.open();
                        this.spinning = true;
                    } else {
                        this.refresh();
                    }
                } else {
                    if (this.spinning) {
                        this.spinnersService.close();
                        this.spinning = false;
                    }
                    if (this.autoRefreshTimeout) {
                        clearTimeout(this.autoRefreshTimeout);
                        this.autoRefreshTimeout = undefined;
                    }
                }
            }
        }));

        if (this.searchPageService) {
            this.subscriptions.add(this.searchPageService.pageRefreshRequested.subscribe(() => {
                this.refresh();
            }));
        }
    }

    @Input() get autoRefreshInterval(): number {
        return this._autoRefreshInterval;
    }

    set autoRefreshInterval(autoRefreshInterval: number) {
        this._autoRefreshInterval = autoRefreshInterval;
        if (this.hasInitialised && this.hasSearchStarted && autoRefreshInterval && !this.autoRefreshTimeout) {
            this.refresh();
        }
    }

    get autoRefreshEnabled(): boolean {
        return this._autoRefreshEnabled;
    }

    set autoRefreshEnabled(value: boolean) {
        this._autoRefreshEnabled = value;
        if (this._autoRefreshEnabled) {
            this.refresh();
        } else if (this.autoRefreshTimeout) {
            clearTimeout(this.autoRefreshTimeout);
            this.autoRefreshTimeout = undefined;
        }
    }

    @Input() set editRoute(value: string) {
        this._editRoute = value;
    }

    get editRoute(): string {
        return this._editRoute ? this._editRoute : this.searchModel.getResource();
    }

    get isSelectMode(): boolean {
        return this.stackedPageContextService != undefined;
    }

    get isMultiSelectMode(): boolean {
        return this.stackedPageContextService.params.searchType === SearchType.MULTIPLE_SELECTION;
    }

    get extendedSearchParams(): ExtendedSearchParams {
        return this.stackedPageContextService != null ? this.stackedPageContextService.params : undefined;
    }

    get actionsTitle(): string {
        if (this.entityActionsComponent && this.entityActionsComponent.first && this.entityActionsComponent.first.title && this.entityActions.length) {
            return this.entityActionsComponent.first.title;
        } else {
            return "wbo.common.label.actions";
        }
    }

    ngOnInit(): void {
        this.searchModel.limit = SearchPageComponent.INITIAL_LIMIT_PAGE;
        this.hasSearchStarted = false;
        if (this.searchOnOpen) {
            setTimeout(() => {
                this.prepareInitialFilter.emit();
                this.searchResults();
            });
        }

        this.subscriptions.add(this.searchModel.filterChange.subscribe(() => {
            this.searchModel.start = 0;
            this.analyticsService.trackEvent(AnalyticEvent.SEARCH_FILTER_UPDATED, this.searchModel);
            this.searchResults();
        }));

        if (this.isSelectMode) {
            this.hasHeaderToggle = false;
        } else if (this.searchGridComponent) {
            this.hasHeaderToggle = true;
        }

        if (this.entityModel) {
            if (this.disableAddButton) {
                this.showAddButton = false;
            }
            if (this.showAddButton == undefined) {
                this.entityModel.canCreateEntity().then((canCreateEntity: boolean) => this.showAddButton = canCreateEntity);
            }
            if (this.disableOpenButton) {
                this.showOpenButton = false;
            }
            if (this.showOpenButton == undefined) {
                if (!!this.editPage) {
                    this.entityModel.canGetEntity().then((canGetEntity: boolean) => {
                        this.showOpenButton = canGetEntity;
                        this.searchPageService.canOpenEntity = this.showOpenButton;
                    });
                } else {
                    this.showOpenButton = false;
                }
            }
            this.searchPageService.canOpenEntity = this.showOpenButton;
        }

        this.hasInitialised = true;
    }

    ngAfterContentInit(): void {
        // subscribe to search grid events
        if (this.searchGridComponent) {
            this.subscriptions.add(this.searchGridComponent.itemAction.subscribe(event => this.handleActionEvent(event)));
            this.subscriptions.add(this.searchGridComponent.pageChange.subscribe(event => this.handlePageChange(event)));
            this.subscriptions.add(this.searchGridComponent.sortChange.subscribe(event => this.handleSortChange(event)));
        }
        this.evaluateEntityActions();
        this.subscriptions.add(this.entityActionsComponent.changes.subscribe(() => this.evaluateEntityActions()));
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }

    evaluateEntityActions(): void {
        if (this.entityActionsSubscription) {
            this.entityActionsSubscription.unsubscribe();
        }
        if (this.entityActionsComponent.length > 0) {
            this.entityActionsSubscription = this.entityActionsComponent.first.actions$.subscribe((actions) => this.entityActions = actions);
        } else {
            this.entityActions = [];
        }
    }

    clickAction(action: EntityActionComponent): void {
        action.clicked();
    }

    handleActionEvent(event: SearchActionEvent): void {
        switch (event.action) {
            case SearchActionEvent.ACTION_EDIT:
                if (this.searchPageService.canOpenEntity) {
                    const actionEvent: ActionEvent = new ActionEvent(event.item);
                    this.editing.emit(actionEvent);
                    if (actionEvent.isDefaultPrevented()) {
                        return;
                    }
                    if (this.isSelectMode) {
                        // select mode means we are in a dialog
                        this.stackedPageService.open(this.editPage, new EditParams(this.searchModel.getIdentifier(event.item), EditMode.EDIT));
                    } else {
                        this.router.navigate(["/main/" + this.editRoute, this.searchModel.getIdentifier(event.item)]);
                    }
                }
                break;
            case SearchActionEvent.ACTION_SELECT_SINGLE:
                this.selectedRecords = [event.item];
                this.analyticsService.trackEvent(AnalyticEvent.SEARCH_SELECT_SINGLE, this.searchModel);
                this.confirmSelection();
                break;
            case SearchActionEvent.ACTION_VIEW:
                // Only expected in select mode
                if (this.searchPageService.canOpenEntity) {
                    if (this.editPage) {
                        this.stackedPageService.open(this.editPage, new EditParams(this.searchModel.getIdentifier(event.item), EditMode.VIEW));
                    } else {
                        this.onOpen.emit(event);
                    }
                    this.analyticsService.trackEvent(AnalyticEvent.SEARCH_VIEW_ROW, this.searchModel);
                }
                break;
            case SearchActionEvent.ACTION_SELECT_RECORDS:
                this.selectedRecords = event.item;
                this.changeDetectorRef.detectChanges();
                break;
            case SearchActionEvent.ACTION_REMOVE:
                // remove
                break;
            case SearchActionEvent.ACTION_START:
                // start action
                break;
            case SearchActionEvent.ACTION_END:
                // complete an action
                this.searchResults();
                break;
            case SearchActionEvent.ACTION_ADD_FROM_RECORD:
                this.newEntity(event.item);
                break;
        }
    }

    handlePageChange(event: PageGridChangeEvent): void {
        this.searchModel.start = event.start;
        this.searchModel.limit = event.limit;
        this.analyticsService.trackEvent(AnalyticEvent.SEARCH_CHANGE_PAGE, this.searchModel);
        this.searchResults();
    }

    handleSortChange(event: SortGridChangeEvent): void {
        const sortFields: SearchSortField[] = event.sortFields.map(function (value) {
            return new SearchSortField(value.field, value.dir);
        });
        this.searchModel.sort = sortFields;
        this.analyticsService.trackEvent(AnalyticEvent.SEARCH_COLUMN_SORT, (this.searchModel.getResource() + (event.sortFields && event.sortFields.length != 0 ? "/" + event.sortFields[0].field : "")));
        this.searchResults();
    }

    public newEntity(params?: any, resource?: string): void {
        const actionEvent: ActionEvent = new ActionEvent();
        this.onAddNew.emit(actionEvent);
        if (!actionEvent.isDefaultPrevented()) {
            if (resource == undefined) {
                resource = this.searchModel.getResource();
            }
            if (this.isSelectMode) {
                this.analyticsService.trackEvent(AnalyticEvent.SEARCH_ADD_NEW_EXTENDED, this.searchModel);
                const editComponent: any = this.routingDiscoveryService.findRoutingComponent(resource);
                this.subscriptions.add(this.stackedPageService.open(editComponent, new EditParams(params, EditMode.ADD, null, this.editParams)).subscribe((entity: EntityData<any>) => {
                    this.searchModel.searchRecords({[this.searchModel.getIdentifierField()]: entity.getIdentifier()},
                        undefined, 2, undefined).subscribe(((searchResult) => {
                        if (searchResult.data.length !== 1) {
                            throw new Error("Error finding the selected record for searchModel " + this.searchModel.getResource());
                        }
                        if (this.isMultiSelectMode) {
                            this.searchResults();
                            this.searchGridComponent.selectRecord(true, searchResult.data[0]);
                        } else {
                            this.selectedRecords = searchResult.data;
                            this.analyticsService.trackEvent(AnalyticEvent.SEARCH_SELECT_SINGLE, this.searchModel);
                            this.confirmSelection();
                        }
                    }));
                }));
            } else {
                this.analyticsService.trackEvent(AnalyticEvent.SEARCH_ADD_NEW, this.searchModel);
                this.router.navigate(["main", resource, EditPageComponent.NEW_ENTITY_PARAM], params ?
                    {queryParams: {params: PageParameterService.encodeParams(params)}} : undefined);
            }
        }
    }

    searchResults(): void {
        this.hasSearchStarted = true;
        this.spinnersService.open(false);
        this.spinning = true;

        if (this.searching) {
            this.requestedSearch = true;
            return;
        }

        const start: number = Date.now();
        this.searching = true;
        this.searchModel.search().subscribe(() => {
            if (this.spinning) {
                this.spinnersService.close();
                this.spinning = false;
            }
            this.analyticsService.trackTiming(AnalyticEvent.SEARCH_TIMING_LOAD, (Date.now() - start), this.searchModel);
            this.searching = false;
            if (this.requestedSearch) {
                this.requestedSearch = false;
                this.searchResults();
            }
            this.setRefreshTimer();
        }, (error) => {
            if (this.spinning) {
                this.spinnersService.close();
                this.spinning = false;
            }
            this.errorHandlerService.displayDataClientError(error);
            this.searching = false;
            if (this.requestedSearch) {
                this.requestedSearch = false;
                this.searchResults();
            }
        });
    }

    setRefreshTimer(): void {
        if (this._autoRefreshInterval) {
            if (this.autoRefreshTimeout) {
                clearTimeout(this.autoRefreshTimeout);
            }
            if (this._autoRefreshEnabled) {
                this.autoRefreshTimeout = setTimeout(() => {
                    this.autoRefreshTimeout = undefined;
                    if (this._autoRefreshInterval && this._autoRefreshEnabled) {
                        this.refresh();
                    }
                }, this._autoRefreshInterval);
            }
        }
    }

    refreshClicked(): void {
        if (this.overrideManualRefresh) {
            this.onManualRefresh.emit();
        } else {
            this.refresh();
        }
    }

    refresh(): void {
        if (this.autoRefreshTimeout) {
            clearTimeout(this.autoRefreshTimeout);
            this.autoRefreshTimeout = undefined;
        }
        this.searchResults();
        this.searchPageService.notifyRefresh();
    }

    cancelSelection(): void {
        this.stackedPageContextService.cancel();
    }

    confirmSelection(): void {
        this.stackedPageContextService.confirm(this.selectedRecords)
    }

    public createModal<T>(modalComponent: Type<T>): Observable<T> {
        return this.modalsService.create(modalComponent, this.injector);
    }

    public destroyModal(modal): Observable<void> {
        return this.modalsService.destroy(modal);
    }
}
